"use client";

import { useState, useEffect, useCallback } from "react";
import { ExternalLink, Play } from "lucide-react";

interface LinkEmbedProps {
  url: string;
}

function extractYouTubeId(url: string): string | null {
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/shorts\/)([a-zA-Z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
}

function YouTubeEmbed({ videoId, url }: { videoId: string; url: string }) {
  const [playing, setPlaying] = useState(false);
  const thumb = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  if (playing) {
    return (
      <div className="mt-2 max-w-md rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700">
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1`}
          className="w-full aspect-video"
          allow="autoplay; encrypted-media"
          allowFullScreen
          title="YouTube video"
        />
      </div>
    );
  }

  return (
    <div className="mt-2 max-w-md rounded-lg overflow-hidden border border-zinc-200 dark:border-zinc-700 group cursor-pointer relative" onClick={() => setPlaying(true)}>
      <img src={thumb} alt="YouTube thumbnail" className="w-full aspect-video object-cover" loading="lazy" />
      <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
        <div className="w-14 h-14 rounded-full bg-red-600 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
          <Play className="w-7 h-7 text-white ml-1" fill="white" />
        </div>
      </div>
      <div className="absolute bottom-0 left-0 right-0 px-3 py-2 bg-gradient-to-t from-black/70 to-transparent">
        <p className="text-xs text-white/80 truncate">youtube.com</p>
      </div>
    </div>
  );
}

// NOTE: Twitter's widgets.js script replaces React-managed `.twitter-tweet`
// blockquotes with its own iframe by detaching the original DOM node. When the
// message containing the embed unmounts (channel switch / scroll), React tries
// to delete the blockquote fiber but its `parentNode` is already null —
// triggering the "Cannot read properties of null (reading 'removeChild')"
// crash captured on PostHog (3 users, 34 occurrences over 7 days).
//
// Removed the live embed in favor of a static link card. If we ever want
// inline tweets back, use react-tweet (server-rendered, no DOM mutation).

interface OgData {
  title?: string;
  description?: string;
  image?: string;
  siteName?: string;
  favicon?: string;
}

function GenericLinkPreview({ url }: { url: string }) {
  const [og, setOg] = useState<OgData | null>(null);
  const [failed, setFailed] = useState(false);

  const fetchOg = useCallback(async () => {
    try {
      // Use a simple proxy/og-fetch approach — try to get OG tags
      const hostname = new URL(url).hostname;
      const favicon = `https://www.google.com/s2/favicons?domain=${hostname}&sz=32`;

      // We'll show a minimal preview with just the domain + favicon
      // Full OG fetching would need a backend proxy to avoid CORS
      setOg({
        siteName: hostname.replace('www.', ''),
        favicon,
      });
    } catch {
      setFailed(true);
    }
  }, [url]);

  useEffect(() => { fetchOg(); }, [fetchOg]);

  if (failed || !og) return null;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="mt-2 flex items-center gap-2.5 px-3 py-2 max-w-md rounded-lg border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors group"
    >
      {og.favicon && <img src={og.favicon} alt="" className="w-4 h-4 flex-shrink-0 rounded-sm" loading="lazy" />}
      <div className="flex-1 min-w-0">
        {og.title && <p className="text-sm font-medium text-zinc-800 dark:text-zinc-200 truncate">{og.title}</p>}
        {og.description && <p className="text-xs text-zinc-500 dark:text-zinc-400 line-clamp-2">{og.description}</p>}
        <p className="text-[10px] text-zinc-400 dark:text-zinc-500 truncate">{og.siteName || url}</p>
      </div>
      {og.image && (
        <img src={og.image} alt="" className="w-16 h-16 rounded object-cover flex-shrink-0" loading="lazy" />
      )}
      <ExternalLink className="w-3.5 h-3.5 text-zinc-400 opacity-0 group-hover:opacity-100 flex-shrink-0 transition-opacity" />
    </a>
  );
}

export function LinkEmbed({ url }: LinkEmbedProps) {
  const ytId = extractYouTubeId(url);
  if (ytId) return <YouTubeEmbed videoId={ytId} url={url} />;

  // Twitter/X falls through to GenericLinkPreview — see comment above
  // TwitterEmbed for why the live widget was removed.

  // Generic link preview for all other URLs
  return <GenericLinkPreview url={url} />;
}
